<script>
	/** @type {string} */
	export let countryPhoneCode = '+48';
	/** @type {boolean} */
	export let disabled = false;
	/** @type {string} */
	export let id = '';
	/** @type {string} */
	export let label = '';
	/** @type {null | number} */
	export let maxlength = null;
	/** @type {null | number} */
	export let minlength = null;
	/** @type {string | undefined} */
	export let name = undefined;
	/** @type {null | string} */
	export let pattern = null;
	/** @type {null | string} */
	export let placeholder = null;
	/** @type {boolean} */
	export let readonly = false;
	/** @type {boolean} */
	export let required = false;
	/** @type {'sm' | 'md' | 'lg'} */
	export let size = 'md';
	/** @type {'date' | 'email' | 'password' | 'search' | 'tel' | 'text' | 'time' | 'url'} */
	export let type = 'text';
	/** @type {any} */
	export let value = null;

	$: tag = $$slots.label || label ? 'label' : 'div';

	/**
	 * @param {HTMLInputElement} node
	 */
	function typeAction(node) {
		node.type = type;
	}

	/**
	 * @param {string} input
	 */
	function formatPhoneNumber(input) {
		if (!input.startsWith(countryPhoneCode) && !input.startsWith(`+${countryPhoneCode}`)) {
			input = countryPhoneCode + input;
		}

		if (!countryPhoneCode || countryPhoneCode !== '+48') return input;

		let cleanNumber = input.replace(/[^\d]/g, '').replace(/^48|^44|^39|^33|^49|^380/, '');

		cleanNumber = cleanNumber.substring(0, 9);

		let firstDigit = parseInt(cleanNumber.charAt(0));
		let formattedNumber;

		if (firstDigit >= 1 && firstDigit <= 4) {
			formattedNumber = cleanNumber.replace(
				/(\d{0,2})(\d{0,3})(\d{0,2})(\d{0,2})/,
				function (_, p1, p2, p3, p4) {
					return [p1, p2, p3, p4].filter((p) => p).join(' ');
				}
			);
		} else {
			formattedNumber = cleanNumber
				.match(/.{1,3}/g)
				?.join(' ')
				.trim();
		}

		return `${countryPhoneCode} ${formattedNumber ?? ''}`;
	}

	/**
	 * @param {CustomEvent} event
	 */
	function handleInput(event) {
		if (type === 'tel') {
			value = formatPhoneNumber(event.target.value);
		}
	}

	$: if (type === 'tel' && value && !value.startsWith(countryPhoneCode)) {
		value = formatPhoneNumber(value);
	}

	$: id = id ? `id-${crypto.randomUUID()}` : crypto.randomUUID();
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
	this={tag}
	class="text-input {size}"
	class:active={value?.length}
	class:disabled
	class:readonly
	on:click
>
	{#if $$slots.label || label}
		<div class="label">
			<slot name="label">
				{label}
			</slot>
		</div>
	{/if}
	<div class="field">
		<slot name="left" />
		<input
			autocomplete="off"
			class:active={value?.length}
			{disabled}
			{id}
			{maxlength}
			{minlength}
			{name}
			{pattern}
			{placeholder}
			{readonly}
			{required}
			bind:value
			on:blur
			on:focus
			on:input={handleInput}
			use:typeAction
		/>
		<slot name="right" />
	</div>
</svelte:element>

<style>.text-input{--hsl-input-border:var(--hsl-gray-light);display:inline-grid;gap:.375rem}.text-input:not(.disabled):not(.readonly):hover{--hsl-input-border:var(--hsl-primary), 0.5}.text-input:not(.disabled):not(.readonly):focus-within{--hsl-input-border:var(--hsl-primary)}.text-input.active:not(.disabled):not(.readonly){--hsl-input-border:var(--hsl-primary)}.text-input.disabled,.text-input.disabled input,.text-input.readonly,.text-input.readonly input{cursor:not-allowed}.text-input :global(.icon){--size:var(--icon-size, 1.25rem)!important}.label,input{font:var(--f-ui-md-medium)}.label{color:hsla(var(--hsl-gray-extra-dark))}.field{align-items:center;display:inline-flex;gap:.25rem;background:hsla(var(--hsl-white));border:2px solid hsla(var(--hsl-input-border))}.sm .field{border-radius:.875rem;padding:0 .625rem;min-height:2.75rem}.md .field{padding:0 .625rem;min-height:3rem}.lg .field{padding:0 .75rem;min-height:4.25rem}.lg .field,.md .field{border-radius:1rem}input{-webkit-appearance:none;-moz-appearance:none;appearance:none;background:0 0;height:100%;outline:0;overflow:hidden;text-overflow:ellipsis;padding:0;width:100%}input:-moz-read-only:not([disabled]){color:hsla(var(--hsl-black),.75)!important}input:read-only:not([disabled]){color:hsla(var(--hsl-black),.75)!important}input::-moz-placeholder{overflow:hidden;text-overflow:ellipsis;white-space:none}input::placeholder{overflow:hidden;text-overflow:ellipsis;white-space:none}</style>
